<template>
  <div>
    <v-toolbar color="page-toolbar">
      <v-toolbar-title class="text-body-large"> Dangers signalés <small>(Administration)</small> </v-toolbar-title>
    </v-toolbar>

    <v-card>
      <v-card-title class="d-flex flex-wrap align-center ga-2">
        Signalements aux administrateurs
        <v-spacer></v-spacer>
        <v-switch
          v-model="showResolved"
          label="Afficher les signalements résolus"
          color="primary"
          density="compact"
          hide-details
        ></v-switch>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="shownFlags"
        :loading="loading"
        :items-per-page="25"
        no-data-text="Aucun signalement à traiter"
        items-per-page-text="Éléments par page"
      >
        <template #[`item.created_at`]="{ item }">{{ formatTimestamp(item.created_at) }}</template>
        <template #[`item.danger`]="{ item }">
          <div v-if="item.danger" class="py-2">
            <div class="font-weight-medium">{{ item.danger.river }} - {{ item.danger.section_name }}</div>
            <div class="text-body-small">{{ dangerSummary(item.danger) }}</div>
            <div class="text-body-small text-medium-emphasis">
              Signalé par {{ (item.danger.created_by && item.danger.created_by.displayName) || "?" }}
              <v-chip v-if="item.danger.archived" size="x-small" class="ml-1">Archivé</v-chip>
            </div>
          </div>
          <span v-else class="text-medium-emphasis">Danger supprimé</span>
        </template>
        <template #[`item.by`]="{ item }">{{ (item.by && item.by.displayName) || "?" }}</template>
        <template #[`item.comment`]="{ item }">
          <span class="flag-comment">{{ item.comment }}</span>
        </template>
        <template #[`item.resolved`]="{ item }">
          <v-chip v-if="item.resolved" size="small" color="success" variant="tonal">Résolu</v-chip>
          <v-chip v-else size="small" color="warning" variant="tonal">À traiter</v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <div class="d-flex flex-wrap ga-1 py-1">
            <v-btn
              v-if="item.danger"
              size="small"
              variant="text"
              icon="mdi-open-in-new"
              title="Voir le danger"
              :to="{ name: 'riviere', params: { slug: item.danger.section_alias }, query: { onglet: 'dangers' } }"
            ></v-btn>
            <v-btn
              v-if="item.danger && !item.danger.archived"
              size="small"
              variant="text"
              icon="mdi-archive-arrow-down-outline"
              title="Archiver le danger"
              @click="archiveDanger(item)"
            ></v-btn>
            <v-btn
              v-if="item.danger"
              size="small"
              variant="text"
              color="error"
              icon="mdi-delete-outline"
              title="Supprimer le danger"
              @click="dangerToDelete = item.danger"
            ></v-btn>
            <v-btn
              size="small"
              variant="tonal"
              :color="item.resolved ? undefined : 'success'"
              @click="setResolved(item, !item.resolved)"
            >
              {{ item.resolved ? "Rouvrir" : "Résolu" }}
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog :model-value="Boolean(dangerToDelete)" max-width="480" persistent>
      <v-card v-if="dangerToDelete" title="Supprimer ce danger?">
        <v-card-text>
          {{ dangerToDelete.river }} - {{ dangerToDelete.section_name }} : {{ dangerSummary(dangerToDelete) }}.
          <br />
          Le danger, ses vérifications et ses photos seront supprimés définitivement. Pour un danger qui n'est plus là,
          archivez-le plutôt.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="deleting" @click="dangerToDelete = null">Annuler</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" @click="deleteDanger">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { auth } from "@/firebase";
  import { DangersService } from "@/store/modules/dangers";
  import { notify, notifyError } from "@/services/Notification.service";
  import { dangerSummary } from "@/utils/dangers";

  // Dangers flagged with "Signaler aux administrateurs": who flagged what and why, and the moderation actions.
  export default {
    name: "DangerFlags",
    data() {
      return {
        loading: true,
        // Flags with their danger (null when it was deleted)
        flags: [],
        showResolved: false,
        dangerToDelete: null,
        deleting: false,
        headers: [
          { title: "Date", key: "created_at", width: 150 },
          { title: "Danger", key: "danger", sortable: false },
          { title: "Commentaire", key: "comment", sortable: false },
          { title: "Signalé par", key: "by", sortable: false },
          { title: "Statut", key: "resolved", width: 110 },
          { title: "", key: "actions", sortable: false, align: "end" },
        ],
      };
    },
    computed: {
      shownFlags() {
        return this.showResolved ? this.flags : this.flags.filter((flag) => !flag.resolved);
      },
    },
    async mounted() {
      await this.load();
    },
    methods: {
      dangerSummary,
      formatTimestamp(timestamp) {
        if (!timestamp || typeof timestamp.toDate !== "function") return "";
        return timestamp.toDate().toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
      },
      async load() {
        this.loading = true;
        try {
          const flags = await DangersService.actions.getDangerFlags();
          const dangerIds = [...new Set(flags.map((flag) => flag.danger_id))];
          const dangers = await Promise.all(dangerIds.map((id) => DangersService.actions.getDanger(id)));
          const dangersById = new Map(dangerIds.map((id, index) => [id, dangers[index]]));
          this.flags = flags.map((flag) => ({ ...flag, danger: dangersById.get(flag.danger_id) || null }));
        } catch (error) {
          console.error("Error loading danger flags:", error);
          notifyError("Les signalements n'ont pas pu être chargés.");
        } finally {
          this.loading = false;
        }
      },
      async setResolved(flag, resolved) {
        try {
          await DangersService.actions.setFlagResolved(flag.id, resolved, auth.currentUser);
          flag.resolved = resolved;
        } catch (error) {
          console.error("Error updating danger flag:", error);
          notifyError("Le signalement n'a pas pu être modifié.");
        }
      },
      async archiveDanger(flag) {
        try {
          await DangersService.actions.setArchived(flag.danger.id, true, auth.currentUser);
          notify("Danger archivé.");
          await this.load();
        } catch (error) {
          console.error("Error archiving danger:", error);
          notifyError("Le danger n'a pas pu être archivé.");
        }
      },
      async deleteDanger() {
        this.deleting = true;
        try {
          await DangersService.actions.deleteDanger(this.dangerToDelete);
          notify("Danger supprimé.");
          this.dangerToDelete = null;
          await this.load();
        } catch (error) {
          console.error("Error deleting danger:", error);
          notifyError("Le danger n'a pas pu être supprimé.");
        } finally {
          this.deleting = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .flag-comment {
    white-space: pre-line;
  }
</style>
